// DE lifecycle (coverage rows 30; AGENT-MENTION): pause, resume and retire through
// `PATCH /digital-employees/{id}`, remove through `POST …/remove` (managers), each behind an
// AlertDialog that says what happens. The server's moves: active → paused (the DE's keys stop
// working until it resumes), paused → active, any → retired, which is final: its seat
// assignment ends and its live keys are revoked; and remove = retire (when not yet) + every key
// revoked + its LiteLLM keys deleted + off the roster, confirmed by typing the DE's name. A DE
// in setup leaves setup only when its LiteLLM key is delivered (de-setup.tsx), so there is no
// "Activate" here. `useLifecycle` is the moves, a `start(move)` and the one dialog; the profile
// header and the roster's rows render every move as a visible control.
// WF-FIX (2026-10-26): every move says what it leaves of a self-hosted DE's Hermes container
// (`containerNote`), and retiring or removing a DE that agents (AG) report to lists them and
// reassigns them to a chosen DE or person before the move, instead of failing with a 422.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  toast,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveXIcon, PauseIcon, PlayIcon, Trash2Icon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { useAgChoices } from "../agents/ag-actions";
import { keys, workforceAgentsQuery } from "../queries";
import { problemText } from "./de-status";

export type Move = "pause" | "resume" | "retire" | "remove";

export const MOVES: Record<
  Move,
  { to: "paused" | "active" | "retired"; label: string; done: string; icon: typeof PauseIcon }
> = {
  pause: { to: "paused", label: "Pause", done: "paused", icon: PauseIcon },
  resume: { to: "active", label: "Resume", done: "resumed", icon: PlayIcon },
  retire: { to: "retired", label: "Retire", done: "retired", icon: ArchiveXIcon },
  remove: { to: "retired", label: "Remove", done: "removed", icon: Trash2Icon },
};

/** What the lifecycle needs of a DE (a roster row or a profile). */
export type LifecycleDe = Pick<DeProfile, "membership_id" | "display_name" | "status"> & {
  seat: { title: string } | null;
  removed_at?: string | null;
  /**
   * WF-FIX: the profile's self-hosted runtime (null: managed; absent: not known, a roster row).
   * A self-hosted DE's container runs outside CompanyOS, so every move says what it leaves.
   */
  self_hosted?: DeProfile["self_hosted"];
};

/** WF-FIX: what a move leaves running for a self-hosted DE (and for one not known to be managed). */
export function containerNote(move: Move, de: LifecycleDe): string | null {
  if (de.self_hosted === null) return null;
  const where = de.self_hosted?.runtime ? ` (${de.self_hosted.runtime.container})` : "";
  const known = de.self_hosted !== undefined;
  const subject = known
    ? `Its Hermes container${where}`
    : "If it is self-hosted, its Hermes container";
  if (move === "resume")
    return `This restores its CompanyOS access. ${subject} is not started by CompanyOS; it must be running in ClawPanel to pick up work.`;
  return `This ${move === "pause" ? "suspends" : "ends"} its CompanyOS access (keys, inbox, runs). ${subject} keeps running; stop it in ClawPanel.`;
}

/** WF-FIX: a reassignment before a retire/remove stopped at one agent (the rest kept). */
export class ReassignError extends Error {
  constructor(
    readonly moved: string[],
    readonly failed: string,
    override readonly cause: unknown,
  ) {
    super(`${failed} was not reassigned`);
    this.name = "ReassignError";
  }
}

/** The moves the server allows from `status` (remove: anything not removed yet). */
export function movesFor(status: DeProfile["status"], removed = false): Move[] {
  if (removed) return [];
  if (status === "active") return ["pause", "retire", "remove"];
  if (status === "paused" || status === "not_deployed") return ["resume", "retire", "remove"];
  if (status === "onboarding") return ["retire", "remove"];
  return ["remove"];
}

function explain(move: Move, de: LifecycleDe): string {
  const name = workerName(de.display_name);
  const seat = de.seat?.title ?? "its seat";
  if (move === "pause")
    return `${name} stops working: new mentions and tasks are not started, scheduled work waits and its ingest and LiteLLM keys are refused until you resume it. Nothing is deleted.`;
  if (move === "resume") return `${name} goes back to work and its keys are accepted again.`;
  if (move === "retire")
    return `Retiring is final. ${name} leaves ${seat}, its live keys are revoked and it cannot be resumed. Its history, cost and scorecards are kept.`;
  return `Removing is final. ${name} is retired${de.status === "retired" ? "" : ` and leaves ${seat}`}, every key it holds is revoked, its LiteLLM keys are deleted, and it leaves the roster. Its history, cost and scorecards are kept.`;
}

/** The lifecycle moves of a DE, a way to start one, and the dialog that confirms it (render once). */
export function useLifecycle(
  api: Api,
  companyId: string,
  de: LifecycleDe,
): { moves: Move[]; start: (move: Move) => void; dialog: ReactNode } {
  const qc = useQueryClient();
  const [move, setMove] = useState<Move | null>(null);
  const [typed, setTyped] = useState("");
  const typedId = useId();
  // WF-FIX: retiring or removing a DE that agents report to would strand them (the API's 422):
  // the dialog lists them and reassigns them to the chosen DE or person first.
  const leaving = move === "retire" || move === "remove";
  const ags = useQuery({ ...workforceAgentsQuery(api, companyId), enabled: leaving });
  const reporting = (ags.data ?? []).filter(
    (a) => a.status !== "retired" && a.reports_to_membership_id === de.membership_id,
  );
  const choices = useAgChoices(api, companyId, leaving && reporting.length > 0);
  const [target, setTarget] = useState("");
  const patch = useMutation({
    mutationFn: async (m: Move) => {
      if (m === "retire" || m === "remove") {
        if (!ags.isSuccess) throw new Error("The agents that report to it could not be read.");
        if (reporting.length && !target) throw new Error("Choose who its agents report to.");
        // One at a time, only the agents still reporting to it (the fresh list): a refusal stops
        // here, the list is reread (the moved ones drop out), and a retry moves only the rest.
        const moved: string[] = [];
        for (const a of reporting) {
          try {
            await api.client.workforceAgents.update({
              params: { co: companyId, id: a.id },
              body: { reports_to_membership_id: target },
            });
          } catch (e) {
            await qc.invalidateQueries({ queryKey: keys.workforceAgents(companyId) });
            throw new ReassignError(moved, a.name, e);
          }
          moved.push(a.name);
        }
        if (moved.length) await qc.invalidateQueries({ queryKey: keys.workforceAgents(companyId) });
      }
      return m === "remove"
        ? api.client.digitalEmployees.remove({
            params: { co: companyId, id: de.membership_id },
            body: { confirm: de.display_name },
          })
        : api.client.digitalEmployees.update({
            params: { co: companyId, id: de.membership_id },
            body: { status: MOVES[m].to },
          });
    },
    onSuccess: (profile, m) => {
      qc.setQueryData(keys.de(companyId, de.membership_id), profile);
      void qc.invalidateQueries({ queryKey: keys.digitalEmployees(companyId) });
      setMove(null);
      toast.add({ title: `${workerName(profile.display_name)} ${MOVES[m].done}` });
      // Facts only the GET carries (self-hosted runtime, AiGentPanel): refetch, never stale.
      void qc.invalidateQueries({ queryKey: keys.de(companyId, de.membership_id) });
    },
  });
  const current = move ? MOVES[move] : null;
  // The short name (as shown) or the stored one both confirm; the API gets the stored one.
  const said = typed.trim().toLowerCase();
  const confirmed =
    (move !== "remove" ||
      said === workerName(de.display_name).trim().toLowerCase() ||
      said === de.display_name.trim().toLowerCase()) &&
    !(
      leaving &&
      (!ags.isSuccess || (reporting.length > 0 && (!target || choices.status !== "ready")))
    );
  const note = move ? containerNote(move, de) : null;
  const targets = [...choices.des.filter((t) => t.id !== de.membership_id), ...choices.people];
  const dialog = (
    <AlertDialog
      open={move !== null}
      onOpenChange={(open) => {
        if (!open && !patch.isPending) setMove(null);
      }}
    >
      <AlertDialogContent size="sm">
        {move && current ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="inline-flex items-center gap-1.5">
                {current.label} {workerName(de.display_name)}?
                {note ? (
                  <InfoTip label="About the Hermes container">
                    <span data-slot="container-note">{note}</span>
                  </InfoTip>
                ) : null}
              </AlertDialogTitle>
              <AlertDialogDescription>{explain(move, de)}</AlertDialogDescription>
            </AlertDialogHeader>
            {leaving && reporting.length ? (
              <div className="flex flex-col gap-1.5" data-slot="reassign-ags">
                <p className="text-sm">
                  Reports to it: {reporting.map((a) => workerName(a.name)).join(", ")}
                </p>
                <label htmlFor={`${typedId}-to`} className="text-xs text-muted-foreground">
                  Report to
                </label>
                <NativeSelect
                  id={`${typedId}-to`}
                  value={target}
                  disabled={choices.status !== "ready"}
                  onChange={(e) => setTarget(e.target.value)}
                >
                  <NativeSelectOption value="">
                    {choices.status === "ready"
                      ? "Choose a DE or a person…"
                      : choices.status === "error"
                        ? "Could not load the choices"
                        : "Loading…"}
                  </NativeSelectOption>
                  {choices.status === "ready"
                    ? targets.map((t) => (
                        <NativeSelectOption key={t.id} value={t.id}>
                          {t.kind === "digital_employee" ? `DE · ${workerName(t.name)}` : t.name}
                        </NativeSelectOption>
                      ))
                    : null}
                </NativeSelect>
                {choices.status === "error" ? (
                  <div
                    role="alert"
                    className="flex items-center gap-2 text-sm text-danger-text"
                    data-slot="reassign-choices-error"
                  >
                    The DEs and people could not be read.
                    <Button size="xs" variant="secondary" onClick={choices.retry}>
                      Retry
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
            {move === "remove" ? (
              <div className="flex flex-col gap-1.5" data-slot="remove-confirm">
                <label htmlFor={typedId} className="text-xs text-muted-foreground">
                  Type <strong className="text-foreground">{workerName(de.display_name)}</strong> to
                  confirm
                </label>
                <Input
                  id={typedId}
                  autoComplete="off"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                />
              </div>
            ) : null}
            {leaving && ags.isError ? (
              <div
                role="alert"
                className="flex items-center gap-2 text-sm text-danger-text"
                data-slot="reporting-ags-error"
              >
                The agents that report to it could not be read.
                <Button size="xs" variant="secondary" onClick={() => void ags.refetch()}>
                  Retry
                </Button>
              </div>
            ) : null}
            {patch.isError ? (
              <p role="alert" className="text-sm text-danger-text" data-slot="lifecycle-error">
                {patch.error instanceof ReassignError
                  ? `${patch.error.moved.length ? `Moved: ${patch.error.moved.map(workerName).join(", ")}. ` : ""}Not moved: ${workerName(patch.error.failed)} (${problemText(patch.error.cause, "refused")}). ${workerName(de.display_name)} was not ${current.done}.`
                  : problemText(
                      patch.error,
                      `${workerName(de.display_name)} could not be ${current.done}.`,
                    )}
              </p>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={patch.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant={move === "retire" || move === "remove" ? "destructive" : "default"}
                disabled={patch.isPending || !confirmed}
                onClick={() => patch.mutate(move)}
              >
                {patch.isPending
                  ? "Saving…"
                  : leaving && reporting.length
                    ? `Reassign ${reporting.length === 1 ? "1 agent" : `${reporting.length} agents`} and ${current.label.toLowerCase()}`
                    : `${current.label} ${workerName(de.display_name)}`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : null}
      </AlertDialogContent>
    </AlertDialog>
  );
  return {
    moves: movesFor(de.status, Boolean(de.removed_at)),
    start: (m) => {
      patch.reset();
      setTyped("");
      setTarget("");
      setMove(m);
    },
    dialog,
  };
}

/** Every allowed move as a button (Pause / Resume secondary, Retire and Remove ghost). */
export function DeLifecycleActions({
  api,
  companyId,
  de,
  size,
}: {
  api: Api;
  companyId: string;
  de: LifecycleDe;
  size?: "sm" | "default";
}) {
  const { moves, start, dialog } = useLifecycle(api, companyId, de);
  if (!moves.length) return null;
  return (
    <>
      {moves.map((m) => {
        const Icon = MOVES[m].icon;
        return (
          <Button
            key={m}
            size={size}
            variant={m === "retire" || m === "remove" ? "ghost" : "secondary"}
            onClick={() => start(m)}
          >
            <Icon aria-hidden="true" />
            {MOVES[m].label}
          </Button>
        );
      })}
      {dialog}
    </>
  );
}
